import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import apiClient from '../lib/api';
import type { BusinessProfile, Service } from '../types';
import { Save, Building2, Clock, MapPin, Briefcase, Sparkles } from 'lucide-react';
import CreateBusinessForm from '../components/CreateBusinessForm';
import GoogleConnectButton from '../components/GoogleConnectButton';
import Toast from '../components/Toast';
import {
  validateDescription,
  validateHours,
  validateLocation,
  validateServiceName,
  validateServicePrice,
  validateServiceDuration,
  validateSystemPromptOptional,
} from '../utils/formValidation';

export default function Dashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [profile, setProfile] = useState<BusinessProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [isGoogleConnected, setIsGoogleConnected] = useState(false);

  // Profile Data (Structured)
  const [description, setDescription] = useState('');
  const [hours, setHours] = useState('');
  const [location, setLocation] = useState('');
  const [services, setServices] = useState<Service[]>([]);
  const [newServiceName, setNewServiceName] = useState('');
  const [newServicePrice, setNewServicePrice] = useState('');
  const [newServiceDuration, setNewServiceDuration] = useState<number>(30);
  
  // Custom Instructions (Free Text)
  const [systemPrompt, setSystemPrompt] = useState('');

  // Validation state for profile, systemPrompt, and service add form
  const [profileFieldErrors, setProfileFieldErrors] = useState<Record<string, string>>({});
  const [systemPromptError, setSystemPromptError] = useState('');
  const [serviceFieldErrors, setServiceFieldErrors] = useState<Record<string, string>>({});
  const [serviceTouched, setServiceTouched] = useState<Record<string, boolean>>({});

  useEffect(() => {
    fetchBusinessProfile();
  }, []);

  // Handle Google Calendar connection callback
  useEffect(() => {
    const googleConnected = searchParams.get('google_connected');
    const errorParam = searchParams.get('error');

    if (googleConnected === 'true') {
      // Show success toast
      setToast({
        message: 'החיבור ל-Google Calendar בוצע בהצלחה!',
        type: 'success',
      });
      
      // Clean URL without reloading
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('google_connected');
      setSearchParams(newSearchParams, { replace: true });
      
      // Refetch profile to get updated connection status (will set isGoogleConnected from API)
      fetchBusinessProfile();
    } else if (errorParam) {
      // Show error toast
      setToast({
        message: `החיבור ל-Google Calendar נכשל: ${errorParam}`,
        type: 'error',
      });
      
      // Clean URL without reloading
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('error');
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const fetchBusinessProfile = async () => {
    try {
      setLoading(true);
      setIsNewUser(false);
      const { data } = await apiClient.get<BusinessProfile>('/api/business');
      setProfile(data);
      
      // Load business_profile (structured data)
      const businessProfile = data.business_profile;
      if (businessProfile) {
        setDescription(businessProfile.description || '');
        setHours(businessProfile.hours || '');
        setLocation(businessProfile.location || '');
        setServices(businessProfile.services || []);
      }
      
      // Load custom instructions
      setSystemPrompt(data.system_prompt || '');
      
      // Set Google Calendar connection status from API response
      setIsGoogleConnected(data.google_calendar_connected || false);
    } catch (err: any) {
      if (err.response?.status === 404) {
        // New user - no business profile yet
        setIsNewUser(true);
      } else {
        setError(err.response?.data?.detail || 'טעינת פרופיל העסק נכשלה');
      }
    } finally {
      setLoading(false);
    }
  };

  const validateServiceForm = () => {
    const nameRes = validateServiceName(newServiceName);
    const priceRes = validateServicePrice(newServicePrice);
    const durationRes = validateServiceDuration(newServiceDuration);
    const errs: Record<string, string> = {};
    if (!nameRes.valid) errs.serviceName = nameRes.message;
    if (!priceRes.valid) errs.servicePrice = priceRes.message;
    if (!durationRes.valid) errs.serviceDuration = durationRes.message;
    setServiceFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleAddService = () => {
    if (!validateServiceForm()) return;
    
    // Check if service with same name already exists
    const exists = services.some(s => 
      (typeof s === 'string' && s === newServiceName.trim()) ||
      (typeof s === 'object' && s.name === newServiceName.trim())
    );
    
    if (exists) {
      setServiceFieldErrors((p) => ({ ...p, serviceName: 'שירות בשם זה כבר קיים' }));
      return;
    }
    
    // Create structured service object
    const newService: Service = {
      name: newServiceName.trim(),
      price: newServicePrice.trim() || undefined,
      duration: newServiceDuration >= 15 ? newServiceDuration : 30,
    };
    
    setServices([...services, newService]);
    setNewServiceName('');
    setNewServicePrice('');
    setNewServiceDuration(30);
    setServiceFieldErrors({});
    setServiceTouched({});
  };

  const handleServiceFieldBlur = (field: string) => {
    setServiceTouched((p) => ({ ...p, [field]: true }));
    if (field === 'serviceName') {
      const res = validateServiceName(newServiceName);
      setServiceFieldErrors((p) => ({ ...p, serviceName: res.valid ? '' : res.message }));
    }
    if (field === 'servicePrice') {
      const res = validateServicePrice(newServicePrice);
      setServiceFieldErrors((p) => ({ ...p, servicePrice: res.valid ? '' : res.message }));
    }
    if (field === 'serviceDuration') {
      const res = validateServiceDuration(newServiceDuration);
      setServiceFieldErrors((p) => ({ ...p, serviceDuration: res.valid ? '' : res.message }));
    }
  };

  const handleRemoveService = (index: number) => {
    setServices(services.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      profileFieldErrors.description ||
      profileFieldErrors.hours ||
      profileFieldErrors.location ||
      systemPromptError
    ) return;
    setSaving(true);
    setError(null);
    setSuccess(false);

    if (!profile) {
      setError('Business profile not found');
      setSaving(false);
      return;
    }

    try {
      // Prepare businessProfile (structured)
      const businessProfile: {
        description?: string;
        hours?: string;
        location?: string;
        services?: Service[];
      } = {};

      if (description.trim()) businessProfile.description = description.trim();
      if (hours.trim()) businessProfile.hours = hours.trim();
      if (location.trim()) businessProfile.location = location.trim();
      if (services.length > 0) businessProfile.services = services;

      await apiClient.put('/api/business', {
        systemPrompt: systemPrompt.trim() || undefined,
        businessProfile: Object.keys(businessProfile).length > 0 ? businessProfile : undefined,
      });
      
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'שמירת פרופיל העסק נכשלה');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">טוען...</div>
      </div>
    );
  }

  // Show create form for new users
  if (isNewUser) {
    return <CreateBusinessForm onSuccess={fetchBusinessProfile} />;
  }

  return (
    <div className="max-w-4xl">
      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <div className="mb-6 sm:mb-8">
        <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-2">פרופיל העסק</h1>
        <p className="text-sm sm:text-base text-gray-600">נהל את פרטי העסק וההנחיות למערכת</p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md">
          <p className="text-sm text-green-600">פרופיל העסק נשמר בהצלחה!</p>
        </div>
      )}

      {/* Google Calendar Connection */}
      <div className="mb-6 sm:mb-8 bg-white rounded-lg border border-gray-200 p-4 sm:p-6 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Google Calendar</h2>
            <p className="text-sm text-gray-600">
              חבר את יומן גוגל שלך כדי לאפשר תכונות אינטגרציה עם היומן
            </p>
          </div>
          <div className="flex-shrink-0">
            <GoogleConnectButton isConnected={isGoogleConnected} />
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Section 1: Business Profile (Structured Inputs) */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 shadow-sm">
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              פרופיל העסק
            </h2>
            <p className="text-sm text-gray-600">מידע מובנה על העסק שלך</p>
          </div>

          {/* Description */}
          <div className="mb-6">
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1.5">
              תיאור
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                const res = validateDescription(e.target.value);
                setProfileFieldErrors((p) => ({ ...p, description: res.valid ? '' : res.message }));
              }}
              onBlur={(e) => {
                const res = validateDescription(e.target.value);
                setProfileFieldErrors((p) => ({ ...p, description: res.valid ? '' : res.message }));
              }}
              rows={3}
              className={`w-full px-3 py-2.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent text-sm sm:text-base ${
                profileFieldErrors.description ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="תיאור קצר של העסק שלך"
            />
            {profileFieldErrors.description && (
              <p className="mt-1 text-sm text-red-600">{profileFieldErrors.description}</p>
            )}
          </div>

          {/* Hours */}
          <div className="mb-6">
            <label htmlFor="hours" className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              שעות פעילות
            </label>
            <input
              id="hours"
              type="text"
              value={hours}
              onChange={(e) => {
                setHours(e.target.value);
                const res = validateHours(e.target.value);
                setProfileFieldErrors((p) => ({ ...p, hours: res.valid ? '' : res.message }));
              }}
              onBlur={(e) => {
                const res = validateHours(e.target.value);
                setProfileFieldErrors((p) => ({ ...p, hours: res.valid ? '' : res.message }));
              }}
              className={`w-full px-3 py-2.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent min-h-[44px] text-sm sm:text-base ${
                profileFieldErrors.hours ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="לדוגמה: ראשון-חמישי: 9:00-18:00, שישי: 9:00-14:00"
            />
            {profileFieldErrors.hours && (
              <p className="mt-1 text-sm text-red-600">{profileFieldErrors.hours}</p>
            )}
          </div>

          {/* Location */}
          <div className="mb-6">
            <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              מיקום
            </label>
            <input
              id="location"
              type="text"
              value={location}
              onChange={(e) => {
                setLocation(e.target.value);
                const res = validateLocation(e.target.value);
                setProfileFieldErrors((p) => ({ ...p, location: res.valid ? '' : res.message }));
              }}
              onBlur={(e) => {
                const res = validateLocation(e.target.value);
                setProfileFieldErrors((p) => ({ ...p, location: res.valid ? '' : res.message }));
              }}
              className={`w-full px-3 py-2.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent min-h-[44px] text-sm sm:text-base ${
                profileFieldErrors.location ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="כתובת העסק או המיקום"
            />
            {profileFieldErrors.location && (
              <p className="mt-1 text-sm text-red-600">{profileFieldErrors.location}</p>
            )}
          </div>

          {/* Services */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-2">
              <Briefcase className="w-4 h-4" />
              שירותים
            </label>
            <p className="text-xs text-gray-500 mb-3">
              הוסף שירותים: <strong>שם</strong>, <strong>מחיר</strong> (קבוע או טווח, למשל "100 ש\"ח" או "100-200 ש\"ח"), ו<strong>משך</strong> בדקות (15-480)
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 mb-2">
              <div className="sm:col-span-5">
                <input
                  type="text"
                  value={newServiceName}
                  onChange={(e) => setNewServiceName(e.target.value)}
                  onBlur={() => handleServiceFieldBlur('serviceName')}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddService();
                    }
                  }}
                  autoComplete="off"
                  className={`w-full px-3 py-2.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent min-h-[44px] text-sm sm:text-base ${
                    serviceTouched.serviceName && serviceFieldErrors.serviceName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="שם השירות (לדוגמה: תספורת, עיסוי)"
                />
              </div>
              <div className="sm:col-span-3">
                <input
                  type="text"
                  value={newServicePrice}
                  onChange={(e) => setNewServicePrice(e.target.value)}
                  onBlur={() => handleServiceFieldBlur('servicePrice')}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddService();
                    }
                  }}
                  autoComplete="off"
                  className={`w-full px-3 py-2.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent min-h-[44px] text-sm sm:text-base ${
                    serviceTouched.servicePrice && serviceFieldErrors.servicePrice ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="מחיר (למשל: 100 או 100-200)"
                />
              </div>
              <div className="sm:col-span-2">
                <input
                  type="number"
                  value={newServiceDuration}
                  onChange={(e) => {
                    const v = parseInt(e.target.value);
                    setNewServiceDuration(isNaN(v) ? 30 : v);
                  }}
                  onBlur={() => handleServiceFieldBlur('serviceDuration')}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddService();
                    }
                  }}
                  min={15}
                  max={480}
                  autoComplete="off"
                  className={`w-full px-3 py-2.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent min-h-[44px] text-sm sm:text-base ${
                    serviceTouched.serviceDuration && serviceFieldErrors.serviceDuration ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="דקות"
                />
              </div>
              <div className="sm:col-span-2">
                <button
                  type="button"
                  onClick={handleAddService}
                  disabled={
                    !validateServiceName(newServiceName).valid ||
                    !validateServicePrice(newServicePrice).valid ||
                    !validateServiceDuration(newServiceDuration).valid
                  }
                  className="w-full px-4 py-2.5 bg-black text-white rounded-md hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-h-[44px] font-medium"
                >
                  הוסף
                </button>
              </div>
            </div>
            {(serviceFieldErrors.serviceName || serviceFieldErrors.servicePrice || serviceFieldErrors.serviceDuration) && (
              <p className="mt-1 text-sm text-red-600">
                {serviceFieldErrors.serviceName || serviceFieldErrors.servicePrice || serviceFieldErrors.serviceDuration}
              </p>
            )}
            <div className="flex flex-wrap gap-2 mt-2">
              {services.map((service, index) => {
                const serviceName = typeof service === 'string' ? service : service.name;
                const servicePrice = typeof service === 'object' && service.price ? service.price : null;
                const serviceDuration = typeof service === 'object' && service.duration ? service.duration : null;
                
                return (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1.5 bg-gray-100 text-gray-700 rounded-md text-sm"
                  >
                    <span>
                      <strong>{serviceName}</strong>
                      {serviceDuration && <span className="mr-1 text-blue-600">({serviceDuration} דק')</span>}
                      {servicePrice && <span className="mr-1 text-gray-500">- {servicePrice}</span>}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveService(index)}
                      className="mr-2 text-gray-500 hover:text-gray-700"
                    >
                      ×
                    </button>
                  </span>
                );
              })}
            </div>
          </div>
        </div>

        {/* Section 2: AI Behavior (Custom Instructions) */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 shadow-sm">
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              התנהגות הבוט / הנחיות מותאמות
            </h2>
            <p className="text-sm text-gray-600">התאם אישית את אופן הפעולה והתגובה של הבוט</p>
          </div>

          <div className="mb-6">
            <label htmlFor="systemPrompt" className="block text-sm font-medium text-gray-700 mb-1.5">
              הנחיות מיוחדות לבוט
            </label>
            <textarea
              id="systemPrompt"
              value={systemPrompt}
              onChange={(e) => {
                setSystemPrompt(e.target.value);
                const res = validateSystemPromptOptional(e.target.value);
                setSystemPromptError(res.valid ? '' : res.message);
              }}
              onBlur={(e) => {
                const res = validateSystemPromptOptional(e.target.value);
                setSystemPromptError(res.valid ? '' : res.message);
              }}
              rows={8}
              className={`w-full px-3 py-2.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent font-mono text-sm sm:text-base ${
                systemPromptError ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="למשל: תמיד תפתח ב'בוקר טוב', אל תדבר על פוליטיקה..."
            />
            <p className="mt-1.5 text-xs text-gray-500">
              ההנחיות האלה יתווספו להנחיות המערכת. השתמש בזה כדי להתאים את התנהגות הבוט, הטון או כללים ספציפיים.
            </p>
            {systemPromptError && (
              <p className="mt-1 text-sm text-red-600">{systemPromptError}</p>
            )}
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-start">
          <button
            type="submit"
            disabled={
              saving ||
              !!(
                profileFieldErrors.description ||
                profileFieldErrors.hours ||
                profileFieldErrors.location ||
                systemPromptError
              )
            }
            className="flex items-center justify-center gap-2 bg-black text-white py-2.5 px-6 rounded-md font-medium hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-h-[44px] w-full sm:w-auto"
          >
            <Save className="w-4 h-4" />
            <span>{saving ? 'שומר...' : 'שמור שינויים'}</span>
          </button>
        </div>
      </form>
    </div>
  );
}
